import {useState} from "react";
import className from "classnames/bind";
import style from "~/features/GscInsight/style/Gsc.module.scss";

const cn = className.bind(style);

const SESSION_COLOR = '#e97316';
const USER_COLOR    = '#0ea5e9';

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Đường cong trơn (Catmull-Rom → Bézier) — cùng thuật toán GscTrendChart. */
function smoothPath(pts) {
	if (pts.length < 2) return '';

	let d = `M ${pts[0].x},${pts[0].y}`;

	for (let i = 0; i < pts.length - 1; i++) {
		const p0 = pts[i - 1] || pts[i];
		const p1 = pts[i];
		const p2 = pts[i + 1];
		const p3 = pts[i + 2] || p2;

		const cp1x = p1.x + (p2.x - p0.x) / 6;
		const cp1y = p1.y + (p2.y - p0.y) / 6;
		const cp2x = p2.x - (p3.x - p1.x) / 6;
		const cp2y = p2.y - (p3.y - p1.y) / 6;

		d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
	}

	return d;
}

/**
 * Biểu đồ xu hướng GA4: phiên (sessions) + người dùng (total_users) theo ngày — SVG thuần,
 * mirror GscTrendChart (2 đường chuẩn hoá theo max riêng, hover tooltip).
 * series: [{date, sessions, total_users, key_events, revenue}].
 */
function GaTrendChart({series = []}) {

	const [hover, setHover] = useState(null);

	if (!series.length) {
		return <div className={cn('chartEmpty')}>Chưa có dữ liệu trong khoảng thời gian này. Nếu vừa kết nối, dữ liệu đang được đồng bộ trong nền.</div>;
	}

	const W = 720;
	const H = 240;
	const P = 26;
	const innerW = W - P * 2;
	const innerH = H - P * 2;
	const baseY  = P + innerH;

	const n = series.length;
	const maxSessions = Math.max(1, ...series.map((d) => d.sessions));
	const maxUsers = Math.max(1, ...series.map((d) => d.total_users));

	const x = (i) => (n === 1 ? W / 2 : P + (innerW * i) / (n - 1));
	const y = (v, max) => P + innerH - (innerH * v) / max;

	const sessionPts = series.map((d, i) => ({x: x(i), y: y(d.sessions, maxSessions)}));
	const userPts    = series.map((d, i) => ({x: x(i), y: y(d.total_users, maxUsers)}));

	const sessionLine = smoothPath(sessionPts);
	const userLine    = smoothPath(userPts);

	const area = (line, pts) =>
		`${line} L ${pts[pts.length - 1].x},${baseY} L ${pts[0].x},${baseY} Z`;

	const ticks = n === 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];
	const last = n - 1;

	const handleMove = (e) => {
		if (n < 1) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
		setHover(n === 1 ? 0 : Math.round(frac * (n - 1)));
	};

	const hoverPct = hover === null ? 0 : (n === 1 ? 50 : (hover / (n - 1)) * 100);

	return (
		<div className={cn('chart')}>
			<div className={cn('legend')}>
				<span className={cn('legendItem')}><span className={cn('dot')} style={{background: SESSION_COLOR}} /> Phiên (tối đa {fmt(maxSessions)})</span>
				<span className={cn('legendItem')}><span className={cn('dot')} style={{background: USER_COLOR}} /> Người dùng (tối đa {fmt(maxUsers)})</span>
			</div>

			<div className={cn('plotWrap')} onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
					<defs>
						<linearGradient id="gaSessionGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={SESSION_COLOR} stopOpacity="0.28" />
							<stop offset="100%" stopColor={SESSION_COLOR} stopOpacity="0" />
						</linearGradient>
						<linearGradient id="gaUserGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor={USER_COLOR} stopOpacity="0.20" />
							<stop offset="100%" stopColor={USER_COLOR} stopOpacity="0" />
						</linearGradient>
					</defs>

					{[0, 0.5, 1].map((g) => (
						<line key={g} x1={P} y1={P + innerH * (1 - g)} x2={W - P} y2={P + innerH * (1 - g)}
							stroke="#eef0f4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
					))}

					{n > 1 && (
						<>
							<path d={area(userLine, userPts)} fill="url(#gaUserGrad)" />
							<path d={userLine} fill="none" stroke={USER_COLOR} strokeWidth="2"
								strokeLinecap="round" strokeLinejoin="round" opacity="0.9"
								vectorEffect="non-scaling-stroke" />

							<path d={area(sessionLine, sessionPts)} fill="url(#gaSessionGrad)" />
							<path d={sessionLine} fill="none" stroke={SESSION_COLOR} strokeWidth="2.5"
								strokeLinecap="round" strokeLinejoin="round"
								vectorEffect="non-scaling-stroke" />

							{hover === null && (
								<>
									<circle cx={sessionPts[last].x} cy={sessionPts[last].y} r="3.5" fill="#fff" stroke={SESSION_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
									<circle cx={userPts[last].x} cy={userPts[last].y} r="3" fill="#fff" stroke={USER_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
								</>
							)}

							{hover !== null && (
								<>
									<line x1={x(hover)} y1={P} x2={x(hover)} y2={baseY}
										stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
									<circle cx={userPts[hover].x} cy={userPts[hover].y} r="3.5" fill="#fff" stroke={USER_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
									<circle cx={sessionPts[hover].x} cy={sessionPts[hover].y} r="4" fill="#fff" stroke={SESSION_COLOR} strokeWidth="2" vectorEffect="non-scaling-stroke" />
								</>
							)}
						</>
					)}

					{n === 1 && (
						<>
							<circle cx={x(0)} cy={y(series[0].total_users, maxUsers)} r="4" fill={USER_COLOR} />
							<circle cx={x(0)} cy={y(series[0].sessions, maxSessions)} r="4" fill={SESSION_COLOR} />
						</>
					)}

					{ticks.map((i) => (
						<text key={i} x={x(i)} y={H - 6} fontSize="10" fill="#9ca3af"
							textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
							{String(series[i].date).slice(5)}
						</text>
					))}
				</svg>

				{hover !== null && (
					<div className={cn('tooltip')} style={{left: `${hoverPct}%`}}>
						<div className={cn('tooltipDate')}>{series[hover].date}</div>
						<div className={cn('tooltipRow')}>
							<span><span className={cn('dot')} style={{background: SESSION_COLOR}} /> Phiên</span>
							<b>{fmt(series[hover].sessions)}</b>
						</div>
						<div className={cn('tooltipRow')}>
							<span><span className={cn('dot')} style={{background: USER_COLOR}} /> Người dùng</span>
							<b>{fmt(series[hover].total_users)}</b>
						</div>
						<div className={cn('tooltipRow')}>
							<span>Sự kiện chính</span>
							<b>{fmt(series[hover].key_events)}</b>
						</div>
						{series[hover].revenue > 0 && (
							<div className={cn('tooltipRow')}>
								<span>Doanh thu</span>
								<b>{fmt(Math.round(series[hover].revenue))}</b>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export default GaTrendChart;
